import * as d3 from "d3";
import React from "react";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";

export interface IGroupedData {
  label: string;
  values: number[];
}
export interface ILineData {
  index: number;
  value: number;
}
interface Props {
  data: IGroupedData[];
  lineData: [number, number][];
}

interface BarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  onMouseEnter: (e: MouseEvent<SVGPathElement>) => void;
  onMouseLeave: () => void;
}

interface Tooltip {
  x: number;
  y: number;
  index: number;
}

function Bar({
  x,
  y,
  width,
  height,
  color,
  onMouseEnter,
  onMouseLeave,
}: BarProps) {
  const radius = height === 0 ? 0 : width * 0.15;
  return (
    <path
      d={`
      m${x},${y + radius}
      a${radius},${radius} 0 0 1 ${radius},${-radius}
      h${width - 2 * radius}
      a${radius},${radius} 0 0 1 ${radius},${radius}
      v${height - radius}
      h-${width}
      z
    `}
      fill={color}
      onMouseEnter={(event) => onMouseEnter(event)}
      onMouseLeave={onMouseLeave}
    />
  );
}
export function GroupedBarChart({ data, lineData }: Props) {
  //  useEffect(() => {}, []);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const axisBottomRef = useRef<SVGGElement>(null);
  const axisLeftRef = useRef<SVGGElement>(null);

  var BottomDivWidth: any = document.getElementById("barchartdiv")?.clientWidth;
  var BottomDivHeight: any =
    document.getElementById("barchartdiv")?.clientHeight;

  const margin = { top: 10, right: 0, bottom: 20, left: 30 };
  const width =( BottomDivWidth - 15 - margin.left - margin.right)||0;
  const height = 333 - margin.top - margin.bottom;

  const labels = data.map(({ label }) => label);
  const sublabels = Object.keys(data[0].values);
  const values = data.map(({ values }) => values).flat();
  const scaleX = d3.scaleBand().domain(labels).range([0, width]).padding(0.2);
  var maxYscale = 0;

  data.map((d) => {
    var localMax = 0;
    d.values.map((v) => {
      localMax += v;

      if (localMax > maxYscale) maxYscale = localMax;
    });
  });
  maxYscale = maxYscale + 10;
  const scaleY = d3.scaleLinear().domain([0, maxYscale]).range([height, 0]);

  const subscaleX = d3
    .scaleBand()
    .domain(sublabels)
    .range([0, scaleX.bandwidth()])
    .padding(0.05);

  const getX = d3.scaleBand().domain(labels).range([0, width]).padding(0.2);

  const getY = d3.scaleLinear().domain([0, maxYscale]).range([height, 0]);

  const linePath =
    d3
      .line()
      .x(
        (d) =>
          (getX(labels[d[0] - 1]) || 0) + margin.left + subscaleX.bandwidth()
      )
      .y((d) => getY(d[1]) + margin.top)(lineData) || undefined;

  useEffect(() => {
    if (axisBottomRef.current) {
      d3.select(axisBottomRef.current).call(d3.axisBottom(scaleX));
    }

    if (axisLeftRef.current) {
      d3.select(axisLeftRef.current).call(d3.axisLeft(scaleY));
    }
  }, [scaleX, scaleY]);

  const colorsForBars = ["#E2D4CB", "#C4C4C4", "#336699", "#274B63"];
  
  const textForLegends = ["Network", "Sales", "AfterSale"];
  var z = d3.scaleOrdinal(colorsForBars);
    //Initialize legend
    var legendItemSize = 12;
    var legendSpacing = 4;
  var xOffset = 150;
  var yOffset = 100;
  d3
  .select('.Legend')
  .select('svg').remove();
    var legend = 
  d3.select('.Legend')
  .append('svg')
        .selectAll('.legendItem')
        .data(textForLegends);
  
  //Create legend items
  legend
  .enter()
  .append('rect')
  .attr('class', 'legendItem')
  .attr('width', legendItemSize)
  .attr('height', legendItemSize)
  
  .style("fill", function(i) {
    return z(i);
})
  .attr('transform',
            (d, i) => {
                var x = xOffset;
                var y = yOffset + (legendItemSize + legendSpacing) * i;
                return `translate(${x}, ${y})`;
            });
            
      
  //Create legend labels
  legend
  .enter()
  .append('text')
  .attr('x', xOffset + legendItemSize + 5)
  .attr('y', (d, i) => yOffset + (legendItemSize + legendSpacing) * i + 12)
  .text((d:any) => {
               return d;
  });  

  return (
    <div className="bar-chart-right" id="barchartdiv">
      <svg
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g ref={axisBottomRef} transform={`translate(0, ${height})`} />
          <g ref={axisLeftRef} />
          {data.map(({ label, values }, groupIndex) => (
            <g
              key={`rect-group-${groupIndex}`}
              transform={`translate(${scaleX(label)}, 0)`}
            >
              <rect
                fill={"black"}
                fillOpacity="0.0"
                stroke={"none"}
                x={getX(String(1)) || 0}
                y={getY(lineData[groupIndex][1])}
                width={subscaleX.bandwidth() * 3}
                height={height - getY(lineData[groupIndex][1])}
                onMouseEnter={(event) => {
                  setTooltip({
                    x: event.clientX,
                    y: event.clientY,
                    index: groupIndex,
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
              />

              {values.map((value, barIndex) => (
                <Bar
                  key={`rect-${barIndex}`}
                  x={subscaleX(String(barIndex)) || 0}
                  y={scaleY(value)}
                  width={subscaleX.bandwidth()}
                  height={height - scaleY(value)}
                  color={colorsForBars[barIndex]}
                  onMouseEnter={(event) => {
                    setTooltip({
                      x: event.clientX,
                      y: event.clientY,
                      index: groupIndex,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
              <circle
                fill={"black"}
                stroke={"none"}
                cx={subscaleX(String(1)) || 0}
                //                cx={ ((getX(labels[(lineData[groupIndex][0]-1)])|| 0) ||0 - ( groupIndex+1 * subscaleX.bandwidth() * scaleX.bandwidth()))}
                cy={getY(lineData[groupIndex][1])}
                r={3}
                onMouseEnter={(event) => {
                  setTooltip({
                    x: event.clientX,
                    y: event.clientY,
                    index: groupIndex,
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
              />
            </g>
          ))}
        </g>

        <path
          className="c-linechart-line__path"
          d={linePath}
          strokeLinecap="round"
          strokeWidth="3"
          stroke="#000"
          fill="none"
        />
      </svg>
      <div className="Legend"></div>
      {tooltip !== null ? (
        <div className="tooltip" style={{ top: tooltip.y, left: tooltip.x }}>
          <span className="tooltip__title">{labels[tooltip.index]}</span>
          <table className="tooltip__table">
            <tbody>
              {" "}
              <tr>
                <td>Network</td>
                <td>{data[tooltip.index].values[0]}</td>
              </tr>
              <tr>
                <td>Sales</td>
                <td>{data[tooltip.index].values[1]}</td>
              </tr>
              <tr>
                <td>AfterSales</td>
                <td>{data[tooltip.index].values[2]}</td>
              </tr>
              <tr>
                <td>Total</td>
                <td>
                  {data[tooltip.index].values[0] +
                    data[tooltip.index].values[1] +
                    data[tooltip.index].values[2]}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
